import { NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, Type } from '@angular/core';
import { KatexOptions, MarkdownModule } from 'ngx-markdown';
import { RecipeBlock } from '../../../model';
import { getIconBgColor } from '../../../editor/extensions/accordion/accordion-icon.util';

const CUSTOM_ICON_REGEX = /\{\{icon:(fa|img):([^:}]+)(?::([^:}]*))?(?::(bg))?\}\}/g;

interface TitleSegment {
  type: 'text' | 'icon';
  text?: string;
  iconType?: 'fa' | 'img';
  name?: string;
  color?: string;
  showBackground?: boolean;
}

@Component({
  selector: 'dojo-recipe-block-renderer',
  templateUrl: './recipe-block-renderer.component.html',
  styleUrls: ['./recipe-block-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet, MarkdownModule],
})
export class RecipeBlockRendererComponent {
  readonly block = input.required<RecipeBlock>();
  readonly contentRenderer = input.required<Type<unknown>>();
  readonly hasBackground = computed(() => this.block().has_background !== false);
  readonly katexOptions: KatexOptions = {
    throwOnError: false,
    strict: 'warn',
  };

  readonly getIconBgColor = getIconBgColor;

  private readonly titleSegmentCache = new Map<string, TitleSegment[]>();

  parseTitleSegments(title: string): TitleSegment[] {
    if (!title) {
      return [];
    }

    const cached = this.titleSegmentCache.get(title);
    if (cached) {
      return cached;
    }

    const segments: TitleSegment[] = [];
    let lastIndex = 0;

    for (const match of title.matchAll(CUSTOM_ICON_REGEX)) {
      if (match.index > lastIndex) {
        segments.push({ type: 'text', text: title.slice(lastIndex, match.index) });
      }

      const iconType = match[1] as 'fa' | 'img';
      const name = match[2];
      const color = match[3] || 'currentColor';
      const bg = match[4] === 'bg';

      segments.push({ type: 'icon', iconType, name, color, showBackground: bg });
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < title.length) {
      segments.push({ type: 'text', text: title.slice(lastIndex) });
    }

    if (segments.length === 0) {
      segments.push({ type: 'text', text: title });
    }

    this.titleSegmentCache.set(title, segments);
    return segments;
  }
}
